/**
 * Site configuration — the single source of truth for every site-level fact.
 *
 * @remarks
 * Nothing downstream may hardcode a URL, a title, or a social handle. The head tags, the JSON-LD
 * graph, the generated `sitemap.xml`, the generated `robots.txt` and the web app manifest all read
 * from here, which is what stops them drifting apart.
 *
 * **This file is loaded by `vite.config.ts`** (through the build plugins), so it — and everything
 * it imports — must use relative import paths. The `@/` alias does not exist yet at the moment Vite
 * bundles its own config. See AGENTS.md § The Thing That Breaks Silently.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Shape of {@link siteConfig}.
 */
export interface SiteConfig {
  /** Canonical origin, no trailing slash. Every absolute URL in the app is built from this. */
  readonly url: string
  /** Site name, used for `og:site_name` and the `WebSite` schema entity. */
  readonly name: string
  /** Short name for the web app manifest and the iOS home-screen label. */
  readonly shortName: string
  /** Default document title, used verbatim on the home route. */
  readonly title: string
  /**
   * Template applied to every other route's title. `%s` is replaced by the route title.
   * Set to `'%s'` to disable.
   */
  readonly titleTemplate: string
  /** Default meta description, used when a route does not supply its own. */
  readonly description: string
  /** BCP 47 language tag for `<html lang>`. */
  readonly language: string
  /** Underscored locale for `og:locale`, e.g. `en_US`. */
  readonly locale: string
  /** Additional locales advertised via `og:locale:alternate`. Empty for single-locale sites. */
  readonly alternateLocales: readonly string[]
  /** Browser UI colour, mirrored into the manifest. */
  readonly themeColor: string
  /** Manifest background colour, shown on the PWA splash screen. */
  readonly backgroundColor: string
  /** Social share image. Dimensions are required — Twitter silently drops cards without them. */
  readonly ogImage: {
    readonly url: string
    readonly width: number
    readonly height: number
    readonly alt: string
    readonly type: string
  }
  /** Twitter Card configuration. Handles include the leading `@`. */
  readonly twitter: {
    readonly card: 'summary' | 'summary_large_image'
    readonly site: string
    readonly creator: string
  }
  /** Crawler directives. */
  readonly robots: {
    /** Applied to every route that does not override `meta.robots`. */
    readonly default: string
    /** Paths written as `Disallow:` lines in the generated `robots.txt`. */
    readonly disallow: readonly string[]
  }
  /** Sitelinks search box, declared on the `WebSite` entity. */
  readonly search: {
    /**
     * Search URL with a literal `{search_term}` placeholder, e.g. `https://example.com/search?q={search_term}`.
     *
     * @remarks
     * Empty disables the feature, which is the right default. Declaring a search box against a page
     * that ignores the query tells a consumer it may send visitors to a URL that does not search.
     */
    readonly urlTemplate: string
  }
  /**
   * Overrides for the `@id` fragments the graph publishes.
   *
   * @remarks
   * Only for a fork replacing a site that already publishes different anchors. An `@id` is the
   * identity of a thing: renaming one makes a consumer see the original entity disappear and an
   * unrelated one take its place, losing whatever association it had accumulated. Omit on a new
   * site and take the package defaults — `#entity`, `#website`, `#primaryimage`.
   */
  readonly anchors?: {
    readonly entity?: string
    readonly website?: string
    readonly primaryImage?: string
  }
  /** Defaults applied to sitemap entries that do not set their own. */
  readonly sitemap: {
    readonly changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
    readonly priority: number
  }
  /** Optional runtime instrumentation. */
  readonly features: {
    /**
     * Report Core Web Vitals through `reportWebVitals` from `@bayudwiyansatria/vue`, wired in
     * `main.ts`. The default sink only logs in development — point it at a real collector.
     */
    readonly webVitals: boolean
    /** Register the service worker in production builds. */
    readonly serviceWorker: boolean
  }
}

/**
 * The live site configuration.
 *
 * @remarks
 * Replace every value here when forking. The URL in particular is load-bearing: it becomes the
 * canonical origin, every `@id` anchor in the structured-data graph, and the absolute sitemap URL
 * inside `robots.txt`. A wrong value there is invisible in development and breaks indexing in
 * production.
 *
 * @example
 * ```ts
 * import { siteConfig } from '@/config/site'
 *
 * const canonical = `${siteConfig.url}/about`
 * ```
 */
export const siteConfig: SiteConfig = {
  url: 'https://vue-boilerplate.pages.dev',
  name: 'Vue Boilerplate',
  shortName: 'VueKit',
  title: 'Vue Boilerplate — Production-ready Vue 3 starter',
  titleTemplate: '%s | Vue Boilerplate',
  description:
    'A production-ready Vue 3 starter with prerendered static output, Schema.org structured data, generated sitemap and robots, and Cloudflare Pages deployment.',
  language: 'en',
  locale: 'en_US',
  alternateLocales: ['id_ID'],
  themeColor: '#0f172a',
  backgroundColor: '#0f172a',
  ogImage: {
    url: '/og-image.png',
    width: 1200,
    height: 630,
    alt: 'Vue Boilerplate — production-ready Vue 3 starter',
    type: 'image/png'
  },
  twitter: {
    card: 'summary_large_image',
    site: '@bayudsatria',
    creator: '@bayudsatria'
  },
  robots: {
    default: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    disallow: ['/api/', '/_functions/']
  },
  search: {
    urlTemplate: ''
  },
  sitemap: {
    changefreq: 'weekly',
    priority: 0.7
  },
  features: {
    webVitals: false,
    serviceWorker: true
  }
}
